import { useQuery } from "@apollo/client";
import { ReactFlowProvider } from "@xyflow/react";
import { format } from "date-fns";
import parse from "html-react-parser";
import { Fragment, ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router";
import { Person } from "../../__generated__/graphql";
import { GetEventById } from "../../api/events";
import { GetGroupById } from "../../api/groups";
import { GetObjectById } from "../../api/objects";
import { GetPersonById } from "../../api/people";
import { GetPlaceById } from "../../api/places";
import Center from "../../components/core/Center/Center";
import Loader from "../../components/core/Loader/Loader";
import Text, { TextTag } from "../../components/core/Text/Text";
import { translate } from "../../localization";
import { parseDate } from "../../utils/date";
import { getImages, getTextEditorValue } from "../../utils/string";
import { FilterCategory } from "../browse/Browse";
import { PlaceType } from "../home/Map";
import { routes } from "../router/utils";
import styles from "./Detail.module.scss";
import FamilyTree from "./family-tree/Tree";
import Map from "./Map";
import Slider, { Image } from "./Slider";

interface DetailProps {
  category: FilterCategory;
}

const Detail = ({ category }: DetailProps) => {
  const i18n = translate();
  const { id } = useParams();

  const { data: object, loading: loadingObject } = useQuery(GetObjectById, {
    skip: category !== FilterCategory.Objects || !id,
    variables: {
      where: { id: id ?? "" },
      hydrateRelationships: true,
    },
  });

  const { data: person, loading: loadingPerson } = useQuery(GetPersonById, {
    skip: category !== FilterCategory.People || !id,
    variables: {
      where: { id: id ?? "" },
      hydrateRelationships: true,
    },
  });

  const { data: place, loading: loadingPlace } = useQuery(GetPlaceById, {
    skip: category !== FilterCategory.Places || !id,
    variables: {
      where: { id: id ?? "" },
      hydrateRelationships: true,
    },
  });

  const { data: group, loading: loadingGroup } = useQuery(GetGroupById, {
    skip: category !== FilterCategory.Groups || !id,
    variables: {
      where: { id: id ?? "" },
      hydrateRelationships: true,
    },
  });

  const { data: event, loading: loadingEvent } = useQuery(GetEventById, {
    skip: category !== FilterCategory.Events || !id,
    variables: {
      where: { id: id ?? "" },
      hydrateRelationships: true,
    },
  });

  if (!id) {
    <Navigate to={routes.browse.index} />;
  }

  switch (category) {
    case FilterCategory.People:
      if (!person?.person) {
        return null;
      }

      return (
        <DetailView
          category={category}
          loading={loadingPerson}
          preTitle={
            person.person.occupation
              ?.map((x) =>
                person.person?.sex === "female" ? x.weiblich : x.name,
              )
              .join(", ") ?? ""
          }
          title={`${person.person.givenName} ${person.person.familyName}`}
          subTitle={getTextEditorValue(person.person.nickname)}
          subTitle2={`${
            person.person.dateOfBirth
              ? format(parseDate(person.person.dateOfBirth), "yyyy")
              : ""
          } ${person.person.dateOfDeath ? ` — ${format(parseDate(person.person.dateOfDeath), "yyyy")}` : ""}`}
          person={person.person as Person}
          images={[
            ...(person.person.images?.flatMap(
              (x) =>
                x.images?.map((y) => ({
                  src: y.bild?.url ?? "",
                  title: y.objekte?.[0]?.title ?? "",
                })) ?? [],
            ) ?? []),
            ...(person.person.images?.flatMap(
              (x) =>
                x.imageObjects?.flatMap(
                  (y) =>
                    y.images?.map((z) => ({
                      src: z.bild?.url ?? "",
                      title: y.title ?? "",
                    })) ?? [],
                ) ?? [],
            ) ?? []),
          ]}
          contentLeft={
            <div className={styles.properties}>
              {person.person.dateOfBirth && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.dateOfBirth}:{" "}
                  </div>
                  <div className={styles.value}>
                    {format(parseDate(person.person.dateOfBirth), "dd.MM.yyyy")}
                  </div>
                </div>
              )}
              {person.person.dateOfDeath && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.dateOfDeath}:{" "}
                  </div>
                  <div className={styles.value}>
                    {format(parseDate(person.person.dateOfDeath), "dd.MM.yyyy")}
                  </div>
                </div>
              )}
              {person.person.mother && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.mother}: </div>
                  <div className={styles.value}>
                    <Link to={`/browse/people/${person.person.mother.id}`}>
                      {`${person.person.mother.givenName} ${person.person.mother.familyName}`}
                    </Link>
                  </div>
                </div>
              )}
              {person.person.father && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.father}: </div>
                  <div className={styles.value}>
                    <Link to={`/browse/people/${person.person.father.id}`}>
                      {`${person.person.father.givenName} ${person.person.father.familyName}`}
                    </Link>
                  </div>
                </div>
              )}
              {(person.person.fatherOf?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.children}: </div>
                  <div className={styles.value}>
                    {person.person.fatherOf?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(person.person.motherOf?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.children}: </div>
                  <div className={styles.value}>
                    {person.person.motherOf?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(person.person.objectAuthor?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.works}: </div>
                  <div className={styles.value}>
                    {person.person.objectAuthor?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {person.person.images &&
                getImages(person.person.images, person.person.id) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.photos}: </div>
                    <div className={styles.value}>
                      {parse(getImages(person.person.images, person.person.id))}
                    </div>
                  </div>
                )}
              {person.person.weblinks &&
                getTextEditorValue(person.person.weblinks) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.webLinks}: </div>
                    <div className={styles.value}>
                      {parse(getTextEditorValue(person.person.weblinks, true))}
                    </div>
                  </div>
                )}
              {(person.person.groupCharacters?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.partOfGroup}:{" "}
                  </div>
                  <div className={styles.value}>
                    {person.person.groupCharacters?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/groups/${x.id}`}>
                          {x.bezeichnung}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(person.person.eventCharacters?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.mainCharacterAt}:{" "}
                  </div>
                  <div className={styles.value}>
                    {person.person.eventCharacters?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(person.person.onEvent?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.onEvent}: </div>
                  <div className={styles.value}>
                    {person.person.onEvent?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
          contentRight={parse(getTextEditorValue(person.person.description))}
        />
      );
    case FilterCategory.Places:
      if (!place?.place) {
        return null;
      }

      return (
        <DetailView
          category={category}
          loading={loadingPlace}
          preTitle={place.place.area?.teilVon?.name ?? ""}
          title={place.place.name ?? ""}
          subTitle={place.place.nativeNames ?? ""}
          latitude={
            (place.place.gps?.split(",").map((num) => Number(num.trim())) ?? [
              0, 0,
            ])[0] ?? null
          }
          longitude={
            (place.place.gps?.split(",").map((num) => Number(num.trim())) ?? [
              0, 0,
            ])[1] ?? null
          }
          images={
            place.place.images?.images?.map((x) => ({
              src: x.bild?.url ?? "",
              title: x.objekte?.[0]?.title ?? "",
            })) ?? []
          }
          markerType={place.place.typen?.[0]?.id as PlaceType}
          contentLeft={
            <div className={styles.properties}>
              {place.place.address && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.address}: </div>
                  <div className={styles.value}>{place.place.address}</div>
                </div>
              )}
              {place.place.area?.teilVon?.name && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.municipality}:{" "}
                  </div>
                  <div className={styles.value}>
                    {place.place.area.teilVon.name}
                  </div>
                </div>
              )}
              {place.place.area?.name && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.faction}: </div>
                  <div className={styles.value}>{place.place.area.name}</div>
                </div>
              )}
              {place.place.gps && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.gps}: </div>
                  <div className={styles.value}>{place.place.gps}</div>
                </div>
              )}
              {(place.place.personenPlaceOfBirth?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.placeOfBirth}:{" "}
                  </div>
                  <div className={styles.value}>
                    {place.place.personenPlaceOfBirth?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {place.place.images &&
                getImages(place.place.images, place.place.id) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.photos}: </div>
                    <div className={styles.value}>
                      {parse(getImages(place.place.images, place.place.id))}
                    </div>
                  </div>
                )}
              {place.place.weblinks &&
                getTextEditorValue(place.place.weblinks) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.webLinks}: </div>
                    <div className={styles.value}>
                      {parse(getTextEditorValue(place.place.weblinks, true))}
                    </div>
                  </div>
                )}
              {(place.place.objectPlace?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.objectPlace}:{" "}
                  </div>
                  <div className={styles.value}>
                    {place.place.objectPlace?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(place.place.objectLocationOnWork?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.onWorks}: </div>
                  <div className={styles.value}>
                    {place.place.objectLocationOnWork?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(place.place.eventPlace?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.eventPlace}: </div>
                  <div className={styles.value}>
                    {place.place.eventPlace?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(place.place.groupPlaces?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.groupPlaces}:{" "}
                  </div>
                  <div className={styles.value}>
                    {place.place.groupPlaces?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/groups/${x.id}`}>
                          {x.bezeichnung}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(place.place.eventLocationOnEvent?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.eventLocationOnEvent}:{" "}
                  </div>
                  <div className={styles.value}>
                    {place.place.eventLocationOnEvent?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(place.place.objectLocationToday?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.locatedHereToday}:{" "}
                  </div>
                  <div className={styles.value}>
                    {place.place.objectLocationToday?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
          contentRight={parse(getTextEditorValue(place.place.description))}
        />
      );
    case FilterCategory.Groups:
      if (!group?.group) {
        return null;
      }

      return (
        <DetailView
          category={category}
          loading={loadingGroup}
          preTitle={getTextEditorValue(group.group.otherTitles)}
          title={group.group.bezeichnung ?? ""}
          subTitle={group.group.periodOfOrigin?.displayName ?? ""}
          images={
            group.group.images?.flatMap(
              (x) =>
                x.images?.map((y) => ({
                  src: y.bild?.url ?? "",
                  title: y.objekte?.[0]?.title ?? "",
                })) ?? [],
            ) ?? []
          }
          contentLeft={
            <div className={styles.properties}>
              {(group.group.typen?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.groupTypes}: </div>
                  <div className={styles.value}>
                    {group.group.typen?.map((x) => x.name).join(", ")}
                  </div>
                </div>
              )}
              {(group.group.characters?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.characters}: </div>
                  <div className={styles.value}>
                    {group.group.characters?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(group.group.places?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.places}: </div>
                  <div className={styles.value}>
                    {group.group.places?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/places/${x.id}`}>{x.name}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(group.group.partOf?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.partOf}: </div>
                  <div className={styles.value}>
                    {group.group.partOf?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/groups/${x.id}`}>
                          {x.bezeichnung}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(group.group.contains?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.contains}: </div>
                  <div className={styles.value}>
                    {group.group.contains?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/groups/${x.id}`}>
                          {x.bezeichnung}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(group.group.owner?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.owner}: </div>
                  <div className={styles.value}>
                    {group.group.owner?.map((x) => x.displayName).join(", ")}
                  </div>
                </div>
              )}
              {(group.group.onEvents?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.onEvents}: </div>
                  <div className={styles.value}>
                    {group.group.onEvents?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(group.group.objectAuthor?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.objectAuthor}:{" "}
                  </div>
                  <div className={styles.value}>
                    {group.group.objectAuthor?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {group.group.images &&
                getImages(group.group.images, group.group.id) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.photos}: </div>
                    <div className={styles.value}>
                      {parse(getImages(group.group.images, group.group.id))}
                    </div>
                  </div>
                )}
              {group.group.weblinks &&
                getTextEditorValue(group.group.weblinks) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.webLinks}: </div>
                    <div className={styles.value}>
                      {parse(getTextEditorValue(group.group.weblinks, true))}
                    </div>
                  </div>
                )}
            </div>
          }
          contentRight={parse(getTextEditorValue(group.group.description))}
        />
      );
    case FilterCategory.Events:
      if (!event?.event) {
        return null;
      }

      return (
        <DetailView
          category={category}
          loading={loadingEvent}
          preTitle={event.event.typen?.map((x) => x.name).join(", ") ?? ""}
          title={event.event.title ?? ""}
          subTitle={event.event.periodOfOrigin?.displayName ?? ""}
          images={
            event.event.images?.flatMap(
              (x) =>
                x.images?.map((y) => ({
                  src: y.bild?.url ?? "",
                  title: y.objekte?.[0]?.title ?? "",
                })) ?? [],
            ) ?? []
          }
          contentLeft={
            <div className={styles.properties}>
              {event.event.periodOfOrigin?.displayName && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.periodOfOriginEvent}:{" "}
                  </div>
                  <div className={styles.value}>
                    {event.event.periodOfOrigin.displayName}
                  </div>
                </div>
              )}
              {event.event.place?.name && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.placeEvent}: </div>
                  <div className={styles.value}>
                    <Link to={`/browse/places/${event.event.place.id}`}>
                      {event.event.place.name}
                    </Link>
                  </div>
                </div>
              )}
              {(event.event.characters?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.charactersEvent}:{" "}
                  </div>
                  <div className={styles.value}>
                    {event.event.characters?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(event.event.partOf?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.partOf}: </div>
                  <div className={styles.value}>
                    {event.event.partOf?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(event.event.contains?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.contains}: </div>
                  <div className={styles.value}>
                    {event.event.contains?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(event.event.similarTo?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.similarToEvent}:{" "}
                  </div>
                  <div className={styles.value}>
                    {event.event.similarTo?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/events/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {event.event.images &&
                getImages(event.event.images, event.event.id) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.photos}: </div>
                    <div className={styles.value}>
                      {parse(getImages(event.event.images, event.event.id))}
                    </div>
                  </div>
                )}
              {event.event.weblinks &&
                getTextEditorValue(event.event.weblinks) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.webLinks}: </div>
                    <div className={styles.value}>
                      {parse(getTextEditorValue(event.event.weblinks, true))}
                    </div>
                  </div>
                )}
            </div>
          }
          contentRight={parse(getTextEditorValue(event.event.description))}
        />
      );
    default:
      if (!object?.object) {
        return null;
      }

      return (
        <DetailView
          category={category}
          loading={loadingObject}
          preTitle={object.object.typ?.[0]?.name ?? ""}
          title={object.object.title ?? ""}
          subTitle={object.object.periodOfOrigin?.displayName ?? ""}
          images={[
            ...(object.object.imageObjects?.[0]?.images?.map((x) => {
              return {
                src: x.bild?.url ?? "",
                title: object.object?.imageObjects?.[0]?.title ?? "",
              };
            }) ?? []),
            ...(object.object.images?.map((x) => ({
              src: x.bild?.url ?? "",
              title: x.objekte?.[0]?.title ?? "",
            })) ?? []),
          ]}
          contentLeft={
            <div className={styles.properties}>
              {object.object.periodOfOrigin?.displayName && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.periodOfOrigin}:{" "}
                  </div>
                  <div className={styles.value}>
                    {object.object.periodOfOrigin.displayName}
                  </div>
                </div>
              )}
              {object.object.place && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.place}: </div>
                  <div className={styles.value}>
                    <Link to={`/browse/places/${object.object.place.id}`}>
                      {object.object.place.name}
                    </Link>
                  </div>
                </div>
              )}
              {object.object.dimensions && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.dimensions}: </div>
                  <div className={styles.value}>{object.object.dimensions}</div>
                </div>
              )}
              {(object.object.eigenschaftenMaterial?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.material}: </div>
                  <div className={styles.value}>
                    {object.object.eigenschaftenMaterial
                      ?.map((x) => x.name)
                      .join(", ")}
                  </div>
                </div>
              )}
              {(object.object.eigenschaftenTechnik?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.technique}: </div>
                  <div className={styles.value}>
                    {object.object.eigenschaftenTechnik
                      ?.map((x) => x.name)
                      .join(", ")}
                  </div>
                </div>
              )}
              {((object.object.authorGroup?.length ?? 0) > 0 ||
                (object.object.authorPerson?.length ?? 0) > 0) && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.authorGroup}:{" "}
                  </div>
                  <div className={styles.value}>
                    {object.object.authorPerson?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                    {object.object.authorGroup?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/groups/${x.id}`}>
                          {x.displayName}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(object.object.partOf?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.partOf}: </div>
                  <div className={styles.value}>
                    {object.object.partOf?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(object.object.contains?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.contains}: </div>
                  <div className={styles.value}>
                    {object.object.contains?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {(object.object.imageObjects?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.photos}: </div>
                  <div className={styles.value}>
                    {object.object.imageObjects?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {object.object.weblinks &&
                getTextEditorValue(object.object.weblinks) && (
                  <div className={styles.property}>
                    <div className={styles.label}>{i18n.browse.webLinks}: </div>
                    <div className={styles.value}>
                      {parse(getTextEditorValue(object.object.weblinks, true))}
                    </div>
                  </div>
                )}
              {(object.object.similarTo?.length ?? 0) > 0 && (
                <div className={styles.property}>
                  <div className={styles.label}>{i18n.browse.similarTo}: </div>
                  <div className={styles.value}>
                    {object.object.similarTo?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/objects/${x.id}`}>{x.title}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
              {object.object.locationToday && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.locationToday}:{" "}
                  </div>
                  <div className={styles.value}>
                    <Link
                      to={`/browse/places/${object.object.locationToday.id}`}
                    >
                      {object.object.locationToday.name}
                    </Link>
                  </div>
                </div>
              )}
              {((object.object.personsOnWork?.length ?? 0) > 0 ||
                (object.object.groupsOnWork?.length ?? 0) > 0 ||
                (object.object.locationOnWork?.length ?? 0) > 0) && (
                <div className={styles.property}>
                  <div className={styles.label}>
                    {i18n.browse.personsOnWork}:{" "}
                  </div>
                  <div className={styles.value}>
                    {object.object.personsOnWork?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/people/${x.id}`}>
                          {`${x.givenName} ${x.familyName}`}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                    {object.object.groupsOnWork?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/groups/${x.id}`}>
                          {x.bezeichnung}
                        </Link>
                        <br />
                      </Fragment>
                    ))}
                    {object.object.locationOnWork?.map((x) => (
                      <Fragment key={x.id}>
                        <Link to={`/browse/places/${x.id}`}>{x.name}</Link>
                        <br />
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
          contentRight={parse(getTextEditorValue(object.object.description))}
        />
      );
  }
};

interface DetailViewProps {
  category: FilterCategory;
  preTitle: string;
  title: string;
  subTitle: string;
  subTitle2?: string;
  images: Image[];
  contentLeft?: ReactNode;
  contentRight?: ReactNode;
  longitude?: number | null;
  latitude?: number | null;
  loading?: boolean;
  markerType?: PlaceType;
  person?: Person | undefined;
}

const DetailView = ({
  category,
  preTitle,
  title,
  subTitle,
  subTitle2,
  images,
  contentLeft,
  contentRight,
  longitude,
  latitude,
  loading,
  markerType = PlaceType.House,
  person,
}: DetailViewProps) => {
  const i18n = translate();

  return (
    <div className={styles.detail}>
      <Center className={styles.breadcrumb}>
        <div className={styles.inner}>
          <Link to={routes.browse.index}>
            {i18n.browse.headline(i18n.browse.headlineCategory[category])}
          </Link>
        </div>
      </Center>
      {!loading && (
        <div>
          <Slider images={images} />
          <Center>
            <div className={styles.headlineWrapper}>
              <Text text={preTitle} size={5} className={styles.preTitle} />
              <Text as={TextTag.H1} text={title} />
              <Text text={subTitle} size={5} className={styles.subTitle} />
              <Text text={subTitle2} size={5} />
            </div>
            <div className={styles.content}>
              <div className={styles.left}>
                {longitude && latitude && (
                  <Map
                    latitude={latitude}
                    longitude={longitude}
                    markerType={markerType}
                  />
                )}
                {contentLeft}
              </div>
              <div className={styles.right}>{contentRight}</div>
            </div>
            {category === FilterCategory.People && person && (
              <ReactFlowProvider>
                <FamilyTree person={person} />
              </ReactFlowProvider>
            )}
          </Center>
        </div>
      )}
      {loading && <Loader />}
    </div>
  );
};

export default Detail;
